import { useApolloClient } from '@apollo/client';
import { message } from 'antd';
import dayjs from 'dayjs';
import { Formik } from 'formik';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import {
    CreateVariantDocument,
    type CreateVariantMutation,
    type CreateVariantMutationVariables,
} from '../../../../api/mutations/createVariant';
import useLoadingButton from '../../../../components/button/useLoadingButton';
import Form from '../../../../components/fields/Form';
import FormAutoTouch from '../../../../components/FormAutoTouch';
import ConsolePageWithHeader from '../../../../layouts/ConsoleLayout/ConsolePageWithHeader';
import useHandleError from '../../../../utilities/useHandleError';
import useVariantAssetValidation from '../../VariantDetailsPage/useVariantAssetValidation';
import AddVariantDetails from './AddVariantDetails';
import { type FormValues } from './types';
import useHandleVariantAssets from './useHandleVariantAssets';
import useVariantValidator from './useVariantValidator';

const initialValues: FormValues = {
    identifier: '',
    modelId: '',
    submodelId: null,
    bodyType: null,
    name: { defaultValue: '', overrides: [] },
    description: { defaultValue: '', overrides: [] },
    order: null,
    isActive: true,
    vehiclePrice: null,
    features: { defaultValue: '', overrides: [] },
    engine: { defaultValue: '', overrides: [] },
    modelYear: null,
    seats: 0,
    transmission: null,
    appleCarPlay: null,
    engineType: null,
    bankCodeMappings: [],
};

const AddVariant = () => {
    const { t } = useTranslation(['variant']);
    const navigate = useNavigate();
    const { setLoading, LoadingButton } = useLoadingButton();
    const apolloClient = useApolloClient();
    const variantAssetValidation = useVariantAssetValidation();
    const validator = useVariantValidator();
    const handleVariantAssets = useHandleVariantAssets();

    const onSubmit = useHandleError<FormValues>(
        async values => {
            /*
            Check the assets first, if any of the new assets is broken / indicated malicious, 
            then don't proceed to create variant.

            To prevent user to create multiple "same" variant if there're some issue when uploading the assets
            then they try it again since the variant is already created before.
            */
            await variantAssetValidation(values);
            const { brochure, images, ...settings } = values;
            setLoading(true);
            message.loading({
                content: t('variant:messages.creationSubmitting'),
                duration: 0,
                key: 'primary',
            });

            const { data } = await apolloClient.mutate<CreateVariantMutation, CreateVariantMutationVariables>({
                mutation: CreateVariantDocument,
                variables: {
                    settings: {
                        ...settings,
                        modelYear: settings.modelYear ? dayjs(settings.modelYear).year() : undefined,
                    },
                },
            });

            await handleVariantAssets(data.localVariant.id, values);

            message.success({
                content: t('variant:messages.creationSuccessful'),
                key: 'primary',
            });

            navigate('/admin/vehicles/variants');
        },
        [variantAssetValidation, setLoading, t, apolloClient, handleVariantAssets, navigate],
        undefined,
        () => {
            setLoading(false);
            message.destroy('primary');
        }
    );

    return (
        <ConsolePageWithHeader
            footer={[
                <LoadingButton key="submit" form="addVariantForm" htmlType="submit" type="primary">
                    {t('variant:actions.create')}
                </LoadingButton>,
            ]}
            onBack={() => navigate('/admin/vehicles/variants')}
            title={t('variant:addTitle')}
        >
            <Formik initialValues={initialValues} onSubmit={onSubmit} validate={validator}>
                {({ handleSubmit }) => (
                    <Form id="addVariantForm" name="addVariantForm" onSubmitCapture={handleSubmit}>
                        <FormAutoTouch />
                        <AddVariantDetails initialValues={initialValues} />
                    </Form>
                )}
            </Formik>
        </ConsolePageWithHeader>
    );
};

export default AddVariant;
